import { call, cancel, fork, take, all, takeLatest, select, put } from 'redux-saga/effects'
import { Saga } from '@redux-saga/types'
import { Store } from 'redux'

import { genFlowId, saveFlowToStorage } from '../utils/flowId'
import {
  analyticsActions,
  appActions,
  appSelectors,
  clientActions,
  codetableActions,
  contractActions,
  modalLoaderActions,
  paymentActions,
  popActions,
  productActions,
  requirementActions,
  scoringActions,
  signingActions,
} from '../redux'
import { dispatch, sagaMiddleware } from '../redux/configureStore'
import { ERRORS } from '../constants/errors'

import { setEnvironmentSaga } from './app/config/config'
import * as analyticsSagas from './analytics/analytics'
import * as appSagas from './app/app'
import * as clientSagas from './client/client'
import * as codetableSagas from './codetable/codetable'
import * as contractSagas from './contract/contract'
import * as paymentSagas from './payment/payment'
import * as popSagas from './pop/pop'
import * as productSagas from './product/product'
import * as requirementSagas from './requirement/requirement'
import * as scoringSagas from './scoring/scoring'
import * as signingSagas from './signing/signing'
import api, { getOpenApiInstance } from '../api/api'

import { AppState } from '../redux/reducers/app/model'
import { eventLogger } from '../utils'
import { ProcessStatus } from '../types'

// action type which cancels a running rootSaga in cancellableSaga
export const CANCEL_ROOT_SAGA = 'CANCEL_ROOT_SAGA'

export function* errorHandler(...args: any[]): Generator {
  const saga: Saga = args[0]

  const processStatus = (yield select(eventLogger.selectProcessStatus)) as ProcessStatus
  const isSessionExpired = yield select(appSelectors.isSessionExpired)

  try {
    eventLogger.logSagaCall(saga.name, processStatus)

    if (isSessionExpired) {
      yield window.console.log('Session expired')
      return
    }
    // @ts-ignore
    yield call(...args)
  } catch (error) {
    const localhost = yield select(appSelectors.selectIsLocalhost)
    if (localhost) {
      yield window.console.log(error)
    }
    yield window.console.log(`Saga ${saga.name} Exception: ${JSON.stringify(error)}`)

    eventLogger.logSagaError(saga.name, processStatus, error)

    if (dispatch) {
      yield put(modalLoaderActions.forceHideModalLoader())
      yield put(appActions.showCentralErrorModal(error.type || ERRORS.TECHNICAL_DIFFICULTIES))
    }
  }
}

export function* createOpenApiDependentSagas(apiInstance: any): Generator {
  const watch = (action: any, saga: any) => takeLatest(action, errorHandler, saga)

  const watchApi = (action: any, saga: any) => takeLatest(action, errorHandler, saga, apiInstance)

  yield all([
    // Analytics
    watch(analyticsActions.setAnalyticsPage, analyticsSagas.submitAnalyticsDataSaga),

    // App
    watch(appActions.prefillForm, appSagas.prefillForm),
    watch(appActions.resetApplication, appSagas.resetApplication),
    watch(appActions.setFlowId, appSagas.setFlowId),
    watchApi(appActions.getSaveReturnUrl, appSagas.getSaveReturnUrl),

    // Client
    watch(clientActions.saveClientInfo, clientSagas.saveClientInfo),
    watch(clientActions.saveAddresses, clientSagas.saveAddresses),
    watch(clientActions.saveClientDetails, clientSagas.saveClientDetails),
    watch(clientActions.saveFinanceProfession, clientSagas.saveFinanceProfession),
    watch(clientActions.saveFinanceEmployer, clientSagas.saveFinanceEmployer),
    watch(clientActions.saveFinanceIncome, clientSagas.saveFinanceIncome),
    watch(clientActions.saveFinanceExpenses, clientSagas.saveFinanceExpenses),
    watch(clientActions.saveClientAdditionalInfo, clientSagas.saveClientAdditionalInfo),
    watch(clientActions.finishBranch, clientSagas.finishBranch),
    watch(clientActions.finishOnline, clientSagas.finishOnline),
    watch(clientActions.identitySendSms, clientSagas.identitySendSms),
    watch(clientActions.startIdentityStatus, clientSagas.startIdentityStatus),

    // pop
    watchApi(popActions.uploadPop, popSagas.uploadPop),
    watchApi(popActions.postStatusPoll, popSagas.postStatusPoll),
    watchApi(popActions.fetchUploadPopStart, popSagas.fetchUploadPopStart),
    watchApi(popActions.processingSetup, popSagas.processingSetup),
    watchApi(popActions.processingPool, popSagas.processingPool),

    // product
    watch(productActions.initLoanConfigurator, productSagas.initLoanConfigurator),
    watch(productActions.saveLoanPreConfigurator, productSagas.saveLoanPreConfigurator),
    watch(productActions.updateLoanConfigurator, productSagas.updateLoanConfigurator),
    watch(productActions.commitLoanConfiguratorRe, productSagas.commitLoanConfiguratorRe),

    // Codetable
    watch(codetableActions.getAddressHints, codetableSagas.getAddressHints),

    // Save and return
    watch(clientActions.prepareCacheAuth, clientSagas.prepareCacheAuth),
    watch(clientActions.resendCacheSmsCode, clientSagas.resendCacheSmsCode),
    watch(clientActions.verifyCacheSmsCode, clientSagas.verifyCacheSmsCode),

    // Contract
    watchApi(contractActions.fetchContract, contractSagas.fetchContract),
    watchApi(contractActions.fetchPreContract, contractSagas.fetchPreContract),
    watchApi(contractActions.createCif, contractSagas.createCifSaga),
    watchApi(contractActions.checkCif, contractSagas.checkCifSaga),
    watchApi(contractActions.printPreContract, contractSagas.printPreContractSaga),
    watchApi(contractActions.startPrintPreContract, contractSagas.startPrintPreContractSaga),
    watchApi(contractActions.signContract, contractSagas.signContract),

    // payment
    watchApi(paymentActions.fetchPennyPayment, paymentSagas.fetchPennyPayment),
    watchApi(paymentActions.pennyPaymentConfirm, paymentSagas.pennyPaymentConfirm),
    watchApi(paymentActions.submitAccountNumber, paymentSagas.submitAccountNumber),

    // requirement
    watchApi(requirementActions.getListOfRequirements, requirementSagas.getListOfRequirements),

    // scoring
    watch(scoringActions.startScoring, scoringSagas.startScoring),
    watch(scoringActions.startScoringLoop, scoringSagas.startScoringLoop),

    // signing
    watch(signingActions.setSigningType, signingSagas.setSigningType),
    watchApi(signingActions.sendSms, signingSagas.sendSms),
    watchApi(signingActions.submitCode, signingSagas.submitVerification),
  ])
}

// business logic saga entry point
export function* rootSaga(): Generator {
  const url = new URL(window.location.href)
  const flowId: string =
    (yield url.searchParams.get('xflowid') as string) ||
    window.localStorage.getItem('flowId') ||
    genFlowId()
  yield put(appActions.setFlowId(flowId))
  saveFlowToStorage(flowId)

  // Take care of app ModalDialogConfiguration
  yield call(errorHandler, setEnvironmentSaga, api.localApi)

  // Setup the wizard with a referer URL
  yield call(appSagas.initWizard)

  // Initialize CBL api and sentry
  const configData: AppState = (yield select(appSelectors.selectCblConfig)) as AppState

  eventLogger.initFromAppConfig(configData, flowId)

  const apiUrl = configData.apiUrl
  api.initOpenApi(apiUrl)
  const apiInstance = getOpenApiInstance()

  yield all([(takeLatest as any)(appActions.showCentralErrorModal, appSagas.showCentralErrorModal)])

  // Check if application working
  const isAppAvailable = (yield call(appSagas.fetchAvailability, apiInstance)) as Boolean

  if (isAppAvailable) {
    // Download all codetables
    yield call(errorHandler, codetableSagas.downloadCodetables)

    // Download loan ranges for loan configurator
    yield call(errorHandler, codetableSagas.getLoanRanges)

    // SET SAGA WATCHERS
    yield fork(createOpenApiDependentSagas, apiInstance)

    // Check if previous loan process exists
    yield call(errorHandler, clientSagas.checkExistingFlow)
  }

  // Hide app modal loader
  yield put(modalLoaderActions.hideModalLoader('appInitModalLoader'))
}

// this saga is to be run by sagaMiddleware in order for HMR to work
// note that when saga HMR is enabled, changes in src/redux will also trigger the cancellation
export function* cancellableSaga(): Generator {
  // start the root saga
  const task: any = yield fork(rootSaga)

  // cancelling mechanism
  yield take(CANCEL_ROOT_SAGA)

  yield cancel(task)
}

// default export, you should start and cancel the rootSaga via resulting sagaManager exclusively
export default function createSagaManager(store: Store) {
  return {
    cancel: () => store.dispatch({ type: CANCEL_ROOT_SAGA }),
    start: () => sagaMiddleware.run(cancellableSaga),
  }
}
